import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { BasicSearchBar, LoadingSpinner } from "../../../components";
import { FilterDropdown } from "./FilterDropdown";
import { HandDropdown } from "./HandDropdown";
import { MovementCard } from "./MovementCard";
import { PitchTypeDropdown } from "./PitchTypeDropdown";
import { SessionInfo } from "./SessionInfo";

const SORT_COLUMNS_EXCLUDED_FROM_DROPDOWN = [
  "dom_hand",
  "session_date",
  "session_num",
];

const SORT_COLUMNS_EXCLUDED_FROM_METRIC_DISPLAY = [
  "movement_num",
  "mocap_pitch_type",
];

interface VisualizerSidebarProps {
  movements: any[];
  pitchTypes: string[];
  domHands: string[];
  pitches: Record<string, any>;
  avatarOffsets: Record<string, { x: number; y: number; z: number }>;
  avatarRotations: Record<string, { x: number; y: number; z: number }>;
  dataColumns: string[];
  colorPalette: string[];
  movementTypeId: number;
  sessionInfo: {
    playerName: string;
    domHand: string;
    sessionId: string;
    sessionDate: string;
  };
  isLoading?: boolean;
  isError?: boolean;
  error?: any;
  onRetry?: () => void;
  onMovementClick: (movementId: string) => void;
  onColorChange: (movementId: string, newColor: string) => void;
  onAvatarOffsetChange: (
    movementId: string,
    axis: string,
    value: number,
  ) => void;
  onAvatarRotationChange: (
    movementId: string,
    axis: string,
    value: number,
  ) => void;
  onResetAvatarPosition: (movementId: string) => void;
  onResetAvatarRotation: (movementId: string) => void;
  onResetAllAvatar: (movementId: string) => void;
}

export const VisualizerSidebar = memo<VisualizerSidebarProps>(
  ({
    movements,
    pitchTypes,
    domHands,
    pitches,
    avatarOffsets,
    avatarRotations,
    dataColumns,
    colorPalette,
    sessionInfo,
    isLoading = false,
    isError = false,
    error,
    onRetry,
    onMovementClick,
    onColorChange,
    onAvatarOffsetChange,
    onAvatarRotationChange,
    onResetAvatarPosition,
    onResetAvatarRotation,
    onResetAllAvatar,
  }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [pitchTypeFilter, setPitchTypeFilter] = useState("all");
    const [handFilter, setHandFilter] = useState("all");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    // Build sort column options from dataColumns, alphabetized by label
    const sortColumns = useMemo(() => {
      return dataColumns
        .filter((col) => !SORT_COLUMNS_EXCLUDED_FROM_DROPDOWN.includes(col))
        .map((col) => ({
          key: col,
          label: col.replace(/_/g, " "),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [dataColumns]);

    const [selectedSortColumn, setSelectedSortColumn] = useState<string | null>(
      null,
    );

    // Get list of enabled metrics for display in MovementCard (any sort column except those already on card)
    const enabledMetrics = useMemo(() => {
      if (
        selectedSortColumn &&
        !SORT_COLUMNS_EXCLUDED_FROM_METRIC_DISPLAY.includes(selectedSortColumn)
      ) {
        return [selectedSortColumn];
      }
      return [];
    }, [selectedSortColumn]);

    const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
    const filterDropdownContainerRef = useRef<HTMLDivElement>(null);

    // Close filter dropdown when clicking outside (icon or dropdown)
    useEffect(() => {
      if (!isFilterDropdownOpen) return;
      const handleClickOutside = (e: MouseEvent) => {
        const target = e.target as Node;
        if (
          filterDropdownContainerRef.current &&
          !filterDropdownContainerRef.current.contains(target)
        ) {
          setIsFilterDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isFilterDropdownOpen]);
    const [expandedMovements, setExpandedMovements] = useState<Set<string>>(
      new Set(),
    );
    const [openColorDropdowns, setOpenColorDropdowns] = useState<Set<string>>(
      new Set(),
    );
    const [openPositionDropdowns, setOpenPositionDropdowns] = useState<
      Set<string>
    >(new Set());

    const getValueFromPitchFrames = useCallback(
      (pitch: any, column: string): any => {
        return pitch?.frames?.[0]?.[column];
      },
      [],
    );

    const getValueFromPitchMetadata = useCallback(
      (pitch: any, column: string): any => {
        return pitch?.metadata?.[column];
      },
      [],
    );

    const getValueFromMovement = useCallback(
      (movement: any, column: string): any => {
        return movement[column];
      },
      [],
    );

    const getColumnValue = useCallback(
      (movement: any, column: string, pitchesData: Record<string, any>) => {
        const pitch = pitchesData[movement.id];

        const frameValue = getValueFromPitchFrames(pitch, column);
        if (frameValue !== undefined && frameValue !== null) {
          return frameValue;
        }

        const metadataValue = getValueFromPitchMetadata(pitch, column);
        if (metadataValue !== undefined && metadataValue !== null) {
          return metadataValue;
        }

        return getValueFromMovement(movement, column);
      },
      [
        getValueFromPitchFrames,
        getValueFromPitchMetadata,
        getValueFromMovement,
      ],
    );

    const areNumericValues = (aValue: any, bValue: any): boolean => {
      return typeof aValue === "number" && typeof bValue === "number";
    };

    const compareNumericValues = (
      aValue: number,
      bValue: number,
      order: "asc" | "desc",
    ): number => {
      const diff = aValue - bValue;
      if (diff === 0) return 0;
      return order === "asc" ? diff : -diff;
    };

    const compareStringValues = (
      aValue: any,
      bValue: any,
      order: "asc" | "desc",
    ): number => {
      const aStr = String(aValue || "").toLowerCase();
      const bStr = String(bValue || "").toLowerCase();
      const diff = aStr.localeCompare(bStr);
      if (diff === 0) return 0;
      return order === "asc" ? diff : -diff;
    };

    const compareValues = useCallback(
      (aValue: any, bValue: any, order: "asc" | "desc") => {
        if (areNumericValues(aValue, bValue)) {
          return compareNumericValues(aValue, bValue, order);
        }
        return compareStringValues(aValue, bValue, order);
      },
      [],
    );

    const sortMovementsByColumns = useCallback(
      (
        movementsList: any[],
        enabledColumns: string[],
        order: "asc" | "desc",
        pitchesData: Record<string, any>,
      ) => {
        return movementsList.sort((a, b) => {
          // Sort by selected columns
          for (const column of enabledColumns) {
            const aValue = getColumnValue(a, column, pitchesData);
            const bValue = getColumnValue(b, column, pitchesData);
            const diff = compareValues(aValue, bValue, order);

            if (diff !== 0) {
              return diff;
            }
          }

          // If all values are equal, fall back to movement ID
          return order === "asc"
            ? a.id.localeCompare(b.id)
            : b.id.localeCompare(a.id);
        });
      },
      [getColumnValue, compareValues],
    );

    const filteredMovements = useMemo(() => {
      let filtered = movements;

      // Filter by search query
      if (searchQuery.trim()) {
        filtered = filtered.filter((movement) =>
          movement.id.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      }

      // Filter by pitch type
      if (pitchTypeFilter !== "all") {
        filtered = filtered.filter(
          (movement) => movement.pitchType === pitchTypeFilter,
        );
      }

      // Filter by hand
      if (handFilter !== "all") {
        filtered = filtered.filter(
          (movement) => movement.domHand === handFilter,
        );
      }

      // Sort movements based on selected sort column
      const enabledColumns = selectedSortColumn ? [selectedSortColumn] : [];

      // If no column is selected, sort by movement ID
      if (enabledColumns.length === 0) {
        return filtered.sort((a, b) =>
          sortOrder === "asc"
            ? a.id.localeCompare(b.id)
            : b.id.localeCompare(a.id),
        );
      }

      // Sort by selected columns using utility function
      return sortMovementsByColumns(
        filtered,
        enabledColumns,
        sortOrder,
        pitches,
      );
    }, [
      movements,
      searchQuery,
      pitchTypeFilter,
      handFilter,
      sortOrder,
      selectedSortColumn,
      pitches,
      sortMovementsByColumns,
    ]);

    const handleSearchChange = (value: string) => {
      setSearchQuery(value);
    };

    const handlePitchTypeFilterChange = (
      e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      setPitchTypeFilter(e.target.value);
    };

    const handleHandFilterChange = (
      e: React.ChangeEvent<HTMLSelectElement>,
    ) => {
      setHandFilter(e.target.value);
    };

    const handleSortOrderChange = (order: "asc" | "desc") => {
      setSortOrder(order);
    };

    const handleSortColumnSelect = (column: string | null) => {
      setSelectedSortColumn(column);
    };

    const handleToggleFilterDropdown = () => {
      setIsFilterDropdownOpen(!isFilterDropdownOpen);
    };

    const handleToggleColorDropdown = (
      movementId: string,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      setOpenColorDropdowns((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(movementId)) {
          newSet.delete(movementId);
        } else {
          newSet.add(movementId);
        }
        return newSet;
      });
    };

    const handleTogglePositionDropdown = (
      movementId: string,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      setOpenPositionDropdowns((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(movementId)) {
          newSet.delete(movementId);
        } else {
          newSet.add(movementId);
        }
        return newSet;
      });
    };

    const handleMovementCardClick = (movementId: string) => {
      // Close all dropdowns when clicking on a movement card
      setOpenColorDropdowns(new Set());
      setOpenPositionDropdowns(new Set());

      // Toggle movement expansion
      setExpandedMovements((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(movementId)) {
          newSet.delete(movementId);
        } else {
          newSet.add(movementId);
        }
        return newSet;
      });

      // Call the original movement click handler
      onMovementClick(movementId);
    };

    return (
      <div
        className="bg-gray-50 overflow-y-auto mx-4 my-5 flex flex-col h-full"
        data-testid="visualizer-sidebar"
      >
        <SessionInfo sessionInfo={sessionInfo} />

        <div className="mb-3" data-testid="search-section">
          <BasicSearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by ID"
            data-testid="search-input"
            aria-label="Search movements by ID"
          />
        </div>

        <PitchTypeDropdown
          pitchTypes={pitchTypes}
          pitchTypeFilter={pitchTypeFilter}
          onPitchTypeFilterChange={handlePitchTypeFilterChange}
        />

        <HandDropdown
          domHands={domHands}
          handFilter={handFilter}
          onHandFilterChange={handleHandFilterChange}
        />

        {/* Movements */}
        <div
          className="flex items-center justify-between"
          data-testid="movements-header"
        >
          <h3 className="text-gray-700 text-sm font-medium my-4">
            Movements ({filteredMovements.length})
          </h3>
          <div className="relative" ref={filterDropdownContainerRef}>
            <AdjustmentsVerticalIcon
              className="w-5 h-5 stroke-2 text-gray-900 cursor-pointer hover:text-gray-600 transition-colors"
              data-testid="filter-icon"
              onClick={handleToggleFilterDropdown}
            />

            <FilterDropdown
              isOpen={isFilterDropdownOpen}
              sortOrder={sortOrder}
              sortColumns={sortColumns}
              selectedSortColumn={selectedSortColumn}
              onSortOrderChange={handleSortOrderChange}
              onSortColumnSelect={handleSortColumnSelect}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1" data-testid="movement-list">
          {isLoading ? (
            <div
              className="p-3 border border-gray-300 rounded-lg mb-2 transition-all duration-200 bg-white"
              data-testid="data-loading-message"
            >
              <div className="text-center">
                <p className="text-base font-semibold text-gray-900 mb-2">
                  Loading data...
                </p>
                <LoadingSpinner />
              </div>
            </div>
          ) : isError ? (
            <div
              className="p-3 border border-red-300 rounded-lg mb-2 bg-red-50"
              data-testid="data-error-message"
            >
              <div className="text-center">
                <p className="text-base font-semibold text-red-900 mb-2">
                  Error loading data
                </p>
                <p className="text-xs text-red-600 mb-3">
                  {error?.message ||
                    "Failed to load movement data. Please try again."}
                </p>
                {onRetry && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onRetry();
                    }}
                    className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    data-testid="retry-button"
                    aria-label="Retry loading movement data"
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          ) : filteredMovements.length === 0 ? (
            <div
              className="p-3 border border-gray-300 rounded-lg mb-2 cursor-pointer transition-all duration-200 bg-white hover:bg-gray-50 hover:border-blue-800"
              data-testid="movement-item-no-data"
            >
              <div className="text-center">
                <p className="text-base font-semibold text-gray-900 mb-2">
                  No data available
                </p>
                <p className="text-xs text-gray-500">
                  No movement data found for this session.
                </p>
              </div>
            </div>
          ) : (
            filteredMovements.map((movement) => (
              <MovementCard
                key={movement.id}
                movement={movement}
                isExpanded={expandedMovements.has(movement.id)}
                avatarOffsets={avatarOffsets}
                avatarRotations={avatarRotations}
                openColorDropdowns={openColorDropdowns}
                openPositionDropdowns={openPositionDropdowns}
                colorPalette={colorPalette}
                pitches={pitches}
                enabledMetrics={enabledMetrics}
                selectedSortColumn={selectedSortColumn}
                onMovementClick={handleMovementCardClick}
                onColorChange={onColorChange}
                onAvatarOffsetChange={onAvatarOffsetChange}
                onAvatarRotationChange={onAvatarRotationChange}
                onResetAvatarPosition={onResetAvatarPosition}
                onResetAvatarRotation={onResetAvatarRotation}
                onResetAllAvatar={onResetAllAvatar}
                onToggleColorDropdown={handleToggleColorDropdown}
                onTogglePositionDropdown={handleTogglePositionDropdown}
              />
            ))
          )}
        </div>
      </div>
    );
  },
);

VisualizerSidebar.displayName = "VisualizerSidebar";
